import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  HiOutlineArchive,
  HiOutlinePencil,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineX,
} from "react-icons/hi";

import ButtonBar from "../../Elements/ButtonBar";
import { UserContext } from "../../../context/UserContext";
import apiFetch from "../../../context/api";

const COIN_DENOMINATIONS = [2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01];
const NOTE_DENOMINATIONS = [5, 10, 20, 50, 100, 200, 500];
const ALL_DENOMINATIONS = [...COIN_DENOMINATIONS, ...NOTE_DENOMINATIONS];

const storageKey = (venueId) => `leafgate_cash_boxes_${venueId || "global"}`;

function createEmptyCounts() {
  return Object.fromEntries(ALL_DENOMINATIONS.map((value) => [String(value), 0]));
}

function defaultBoxForm() {
  return { name: "", zone: "Todas", collaborators: [] };
}

// Objetivo orientativo de cambio a dejar en la caja al cerrar (la retirada = total - cambio).
const IDEAL_CHANGE = 135;

function defaultSessionForm() {
  return {
    openingBalance: "",
    expectedBalance: "",
    notes: "",
    counts: createEmptyCounts(),
    changeAmount: "",
  };
}

function sessionDraftKey(boxId, mode) {
  return `${mode}:${String(boxId)}`;
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function calculateCountsTotal(counts) {
  return ALL_DENOMINATIONS.reduce((sum, denomination) => {
    const qty = Number(counts?.[String(denomination)] || 0);
    return sum + denomination * qty;
  }, 0);
}

function normalizeCounts(raw) {
  const next = createEmptyCounts();
  ALL_DENOMINATIONS.forEach((denomination) => {
    next[String(denomination)] = Number(raw?.[String(denomination)] || 0);
  });
  return next;
}

function getStoredBoxes(venueId) {
  try {
    const raw = localStorage.getItem(storageKey(venueId));
    const parsed = JSON.parse(raw || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistBoxes(venueId, boxes) {
  localStorage.setItem(storageKey(venueId), JSON.stringify(boxes));
}

function getCollaboratorName(member) {
  if (!member) return "";
  if (member.profile?.first_name || member.profile?.last_name) {
    return `${member.profile?.first_name || ""} ${member.profile?.last_name || ""}`.trim();
  }
  return member.email || member.name || "";
}

function sortHistoryNewestFirst(history) {
  return [...history].sort((a, b) => {
    const left = new Date(b.closedAt || b.openedAt || 0).getTime();
    const right = new Date(a.closedAt || a.openedAt || 0).getTime();
    return left - right;
  });
}

function mapApiCountsToObject(entries) {
  const next = createEmptyCounts();
  if (!Array.isArray(entries)) return next;
  entries.forEach((entry) => {
    const key = String(entry?.denomination ?? "");
    if (!Object.prototype.hasOwnProperty.call(next, key)) return;
    next[key] = Number(entry?.quantity || 0);
  });
  return next;
}

function mapCountsToApiEntries(counts) {
  return ALL_DENOMINATIONS.map((denomination) => {
    const key = String(denomination);
    const quantity = Math.max(0, Number(counts?.[key] || 0));
    return {
      denomination: key,
      quantity,
      total: Number((denomination * quantity).toFixed(2)),
    };
  }).filter((entry) => entry.quantity > 0);
}

function mapApiSession(session) {
  if (!session) return null;
  return {
    id: session.id,
    status: session.closed_at ? "closed" : "open",
    openedAt: session.opened_at,
    closedAt: session.closed_at,
    openingBalance: Number(session.opening_balance || 0),
    openingCounts: mapApiCountsToObject(session.opening_counts),
    openingCountedTotal: Number(session.opening_total || 0),
    expectedBalance: Number(session.expected_balance || 0),
    closingCounts: mapApiCountsToObject(session.closing_counts),
    closingCountedTotal: Number(session.closing_total || 0),
    difference: Number(session.difference || 0),
    changeTotal: Number(session.change_total || 0),
    withdrawal: Number(session.withdrawal || 0),
    notes: session.notes || "",
    closingNotes: session.notes || "",
  };
}

function mapApiBox(box, collaboratorMap) {
  const collaboratorIds = Array.isArray(box.collaborator_user_ids)
    ? box.collaborator_user_ids.map(String)
    : [];
  return {
    id: box.id,
    name: box.name,
    zone: box.zone || "Todas",
    collaboratorIds,
    collaborators: collaboratorIds.map((id) => collaboratorMap.get(id) || id),
    createdAt: box.created_at,
    updatedAt: box.updated_at,
    defaultOpeningBalance: Number(box.default_opening_balance || 0),
    currentSession: mapApiSession(box.current_session),
    sessions: Array.isArray(box.recent_sessions) ? box.recent_sessions.map(mapApiSession) : [],
  };
}

function DenominationFieldset({ title, denominations, counts, onChange }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 text-sm font-semibold text-gray-800">{title}</div>
      <div className="space-y-2">
        {denominations.map((denomination) => {
          const key = String(denomination);
          const qty = Number(counts?.[key] || 0);
          return (
            <div key={key} className="grid grid-cols-[84px_minmax(0,1fr)_100px] items-center gap-2">
              <div className="rounded-lg bg-gray-100 px-3 py-2 text-right font-semibold text-gray-700">
                {denomination >= 5 ? `${denomination}€` : `${denomination.toFixed(2)}€`}
              </div>
              <input
                type="number"
                min="0"
                step="1"
                value={qty}
                onChange={(event) => onChange(key, event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
              />
              <div className="rounded-lg bg-emerald-50 px-3 py-2 text-right text-sm font-medium text-emerald-800">
                {formatMoney(denomination * qty)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModalShell({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/45 p-4">
      <div className="w-full max-w-5xl rounded-2xl border border-gray-200 bg-[#f7fbf2] shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="text-2xl font-semibold text-gray-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-gray-300 p-2 text-gray-600 hover:bg-gray-100"
          >
            <HiOutlineX size={20} />
          </button>
        </div>
        <div className="max-h-[85vh] overflow-auto p-5">{children}</div>
      </div>
    </div>
  );
}

export default function CajasPage() {
  const userContext = useContext(UserContext);
  const token = userContext[0];
  const venueID = userContext[3];
  const venueName = userContext[4];
  const accessPrivileges = userContext[7];
  const [boxes, setBoxes] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalState, setModalState] = useState({ type: null, boxId: null, sessionId: null });
  const [boxForm, setBoxForm] = useState(defaultBoxForm());
  const [sessionForm, setSessionForm] = useState(defaultSessionForm());
  const [sessionDrafts, setSessionDrafts] = useState({});
  const fetchRequestRef = useRef(0);

  const apiHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  useEffect(() => {
    let cancelled = false;

    async function fetchCollaborators() {
      if (!token || !venueID) {
        setCollaborators([]);
        return;
      }

      try {
        const [staffResponse, ownersResponse] = await Promise.allSettled([
          apiFetch(`/v1/staff?venue_id=${encodeURIComponent(venueID)}`, { headers: apiHeaders }),
          apiFetch(`/v1/owners?venue_id=${encodeURIComponent(venueID)}`, { headers: apiHeaders }),
        ]);

        const collected = [];
        for (const result of [staffResponse, ownersResponse]) {
          if (result.status !== "fulfilled") continue;
          const response = result.value;
          if (!response.ok) continue;
          const payload = await response.json().catch(() => []);
          const list = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.users)
              ? payload.users
              : Array.isArray(payload?.staff)
                ? payload.staff
                : Array.isArray(payload?.owners)
                  ? payload.owners
                  : [];
          list.forEach((member) => {
            const name = getCollaboratorName(member);
            if (!name || !member?.id) return;
            collected.push({
              id: String(member.id),
              name,
            });
          });
        }

        const deduped = Array.from(
          new Map(collected.map((item) => [String(item.id), item])).values()
        );
        if (!cancelled) setCollaborators(deduped);
      } catch {
        if (!cancelled) setCollaborators([]);
      }
    }

    fetchCollaborators();
    return () => {
      cancelled = true;
    };
  }, [apiHeaders, token, venueID]);

  const collaboratorMap = useMemo(
    () => new Map(collaborators.map((item) => [String(item.id), item.name])),
    [collaborators]
  );
  // Cierres ya registrados: solo owner/superadmin (auditoría).
  const canEditCajaMoney = accessPrivileges === "owner" || accessPrivileges === "superadmin";
  // Caja ABIERTA (monedero de apertura): cualquiera que gestione la caja (incl. staff)
  // puede corregir su propio recuento si se equivoca.
  const canEditOpenCaja =
    canEditCajaMoney || accessPrivileges === "staff";

  const refreshBoxes = useCallback(async () => {
    if (!token || !venueID) {
      setBoxes([]);
      return;
    }

    const requestId = fetchRequestRef.current + 1;
    fetchRequestRef.current = requestId;
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await apiFetch(`/v1/cajas?venue_id=${encodeURIComponent(venueID)}`, {
        headers: apiHeaders,
      });
      if (!response.ok) {
        throw new Error("No se pudo cargar la gestión de cajas.");
      }
      const payload = await response.json();
      const items = Array.isArray(payload?.items) ? payload.items : [];
      const mapped = items.map((box) => mapApiBox(box, collaboratorMap));
      if (fetchRequestRef.current === requestId) {
        setBoxes(mapped);
        persistBoxes(venueID, mapped);
      }
    } catch {
      const fallback = getStoredBoxes(venueID);
      if (fetchRequestRef.current === requestId) {
        setBoxes(fallback);
        setErrorMessage(
          fallback.length
            ? "No se pudo refrescar la API de cajas. Se muestra la última copia local."
            : "No se pudo cargar la gestión de cajas."
        );
      }
    } finally {
      if (fetchRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [apiHeaders, collaboratorMap, token, venueID]);

  useEffect(() => {
    refreshBoxes();
  }, [refreshBoxes]);

  const activeBox = useMemo(
    () => boxes.find((box) => box.id === modalState.boxId) || null,
    [boxes, modalState.boxId]
  );

  const history = useMemo(() => {
    const sessions = boxes.flatMap((box) =>
      (box.sessions || []).map((session) => ({
        ...session,
        boxId: box.id,
        boxName: box.name,
        zone: box.zone,
        collaborators: box.collaborators || [],
      }))
    );
    return sortHistoryNewestFirst(sessions);
  }, [boxes]);

  // Filas del Historial = sesiones ABIERTAS (caja abierta) + cierres registrados.
  const historyRows = useMemo(() => {
    const openRows = boxes
      .filter((box) => box.currentSession?.status === "open")
      .map((box) => ({
        ...box.currentSession,
        boxId: box.id,
        boxName: box.name,
        zone: box.zone,
        collaborators: box.collaborators || [],
        isOpen: true,
      }));
    return sortHistoryNewestFirst([...openRows, ...history]);
  }, [boxes, history]);

  const activeHistorySession = useMemo(() => {
    if (!modalState.sessionId) return null;
    return history.find((item) => item.id === modalState.sessionId) || null;
  }, [history, modalState.sessionId]);

  const summary = useMemo(() => {
    const openCount = boxes.filter((box) => box.currentSession?.status === "open").length;
    const totalDifference = history.reduce((sum, item) => sum + Number(item.difference || 0), 0);
    return {
      totalBoxes: boxes.length,
      openCount,
      closedSessions: history.length,
      totalDifference,
    };
  }, [boxes, history]);

  function resetModal() {
    setModalState({ type: null, boxId: null, sessionId: null });
    setBoxForm(defaultBoxForm());
    setSessionForm(defaultSessionForm());
  }

  function closeSessionModal({ preserveDraft = false } = {}) {
    if (
      preserveDraft &&
      modalState.boxId &&
      (
        modalState.type === "open-session" ||
        modalState.type === "close-session" ||
        modalState.type === "edit-open-session" ||
        modalState.type === "edit-closed-session"
      )
    ) {
      const key = sessionDraftKey(modalState.boxId, modalState.type);
      setSessionDrafts((prev) => ({
        ...prev,
        [key]: {
          ...sessionForm,
          counts: normalizeCounts(sessionForm.counts),
          changeAmount: sessionForm.changeAmount ?? "",
        },
      }));
    }

    setModalState({ type: null, boxId: null, sessionId: null });
    setBoxForm(defaultBoxForm());
    setSessionForm(defaultSessionForm());
  }

  function openCreateBoxModal() {
    setBoxForm(defaultBoxForm());
    setModalState({ type: "create-box", boxId: null });
  }

  function openEditBoxModal(box) {
    setBoxForm({
      name: box.name || "",
      zone: box.zone || "Todas",
      collaborators: Array.isArray(box.collaboratorIds) ? box.collaboratorIds : [],
    });
    setModalState({ type: "edit-box", boxId: box.id });
  }

  function openSessionModal(box, mode) {
    const draft = sessionDrafts[sessionDraftKey(box.id, mode)];
    if (draft) {
      setSessionForm({
        openingBalance: draft.openingBalance ?? "",
        expectedBalance: draft.expectedBalance ?? "",
        notes: draft.notes ?? "",
        counts: normalizeCounts(draft.counts),
        changeAmount: draft.changeAmount ?? "",
      });
    } else {
      const baseCounts = normalizeCounts(
        mode === "close-session" ? box.currentSession?.openingCounts : createEmptyCounts()
      );
      setSessionForm({
        openingBalance:
          mode === "open-session"
            ? String(box.defaultOpeningBalance || calculateCountsTotal(baseCounts) || "")
            : "",
        expectedBalance:
          mode === "close-session"
            ? String(box.currentSession?.expectedBalance ?? box.currentSession?.openingBalance ?? calculateCountsTotal(baseCounts))
            : "",
        notes: "",
        counts: baseCounts,
        // Cambio a dejar: por defecto el objetivo orientativo (135) al cerrar.
        changeAmount: mode === "close-session" ? String(IDEAL_CHANGE) : "",
      });
    }
    setModalState({ type: mode, boxId: box.id, sessionId: null });
  }

  function openEditHistorySessionModal(item) {
    const draft = sessionDrafts[sessionDraftKey(item.boxId, "edit-closed-session")];
    if (draft) {
      setSessionForm({
        openingBalance: draft.openingBalance ?? "",
        expectedBalance: draft.expectedBalance ?? "",
        notes: draft.notes ?? "",
        counts: normalizeCounts(draft.counts),
        changeAmount: draft.changeAmount ?? "",
      });
    } else {
      setSessionForm({
        openingBalance: "",
        expectedBalance: String(item.expectedBalance ?? ""),
        notes: item.closingNotes || item.notes || "",
        counts: normalizeCounts(item.closingCounts),
        changeAmount: item.changeTotal != null ? String(item.changeTotal) : "",
      });
    }
    setModalState({ type: "edit-closed-session", boxId: item.boxId, sessionId: item.id });
  }

  function openEditOpenSessionModal(box) {
    const draft = sessionDrafts[sessionDraftKey(box.id, "edit-open-session")];
    if (draft) {
      setSessionForm({
        openingBalance: draft.openingBalance ?? "",
        expectedBalance: draft.expectedBalance ?? "",
        notes: draft.notes ?? "",
        counts: normalizeCounts(draft.counts),
        changeAmount: draft.changeAmount ?? "",
      });
    } else {
      setSessionForm({
        openingBalance: String(box.currentSession?.openingBalance ?? ""),
        expectedBalance: String(box.currentSession?.expectedBalance ?? box.currentSession?.openingBalance ?? ""),
        notes: box.currentSession?.notes || "",
        counts: normalizeCounts(box.currentSession?.openingCounts),
        changeAmount: "",
      });
    }
    setModalState({ type: "edit-open-session", boxId: box.id, sessionId: box.currentSession?.id || null });
  }

  async function upsertBox(event) {
    event.preventDefault();
    if (!token || !venueID) return;
    if (!boxForm.name.trim()) {
      setErrorMessage("El nombre de la caja es obligatorio.");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    try {
      const method = modalState.type === "create-box" ? "POST" : "PATCH";
      const path =
        modalState.type === "create-box" ? "/v1/cajas" : `/v1/cajas/${activeBox?.id}`;
      const body =
        modalState.type === "create-box"
          ? {
              venue_id: venueID,
              name: boxForm.name.trim(),
              zone: boxForm.zone.trim() || "Todas",
              collaborator_user_ids: [...boxForm.collaborators].sort(),
            }
          : {
              name: boxForm.name.trim(),
              zone: boxForm.zone.trim() || "Todas",
              collaborator_user_ids: [...boxForm.collaborators].sort(),
            };

      const response = await apiFetch(path, {
        method,
        headers: apiHeaders,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || "No se pudo guardar la caja.");
      }

      await refreshBoxes();
      resetModal();
    } catch (error) {
      setErrorMessage(error.message || "No se pudo guardar la caja.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteBox() {
    if (!activeBox || !token) return;
    setSaving(true);
    setErrorMessage("");
    try {
      const response = await apiFetch(`/v1/cajas/${activeBox.id}`, {
        method: "DELETE",
        headers: apiHeaders,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || "No se pudo eliminar la caja.");
      }
      await refreshBoxes();
      resetModal();
    } catch (error) {
      setErrorMessage(error.message || "No se pudo eliminar la caja.");
    } finally {
      setSaving(false);
    }
  }

  async function saveSession(event) {
    event.preventDefault();
    if (!activeBox || !token) return;

    setSaving(true);
    setErrorMessage("");
    try {
      const isOpenAction = modalState.type === "open-session";
      const isEditOpenSession = modalState.type === "edit-open-session";
      const isEditClosedSession = modalState.type === "edit-closed-session";
      const endpoint = isOpenAction
        ? `/v1/cajas/${activeBox.id}/open`
        : isEditOpenSession
          ? `/v1/cajas/${activeBox.id}/open-session`
        : isEditClosedSession
          ? `/v1/cajas/${activeBox.id}/sessions/${modalState.sessionId}`
          : `/v1/cajas/${activeBox.id}/close`;
      const countedTotal = calculateCountsTotal(sessionForm.counts);
      const body = isOpenAction
        ? {
            opening_balance: Number(sessionForm.openingBalance || countedTotal || 0),
            expected_balance: Number(sessionForm.openingBalance || countedTotal || 0),
            opening_counts: mapCountsToApiEntries(sessionForm.counts),
            notes: sessionForm.notes?.trim() || "",
          }
        : isEditOpenSession
          ? {
              opening_balance: Number(sessionForm.openingBalance || countedTotal || 0),
              expected_balance: Number(
                sessionForm.expectedBalance || activeBox.currentSession?.expectedBalance || activeBox.currentSession?.openingBalance || countedTotal || 0
              ),
              opening_counts: mapCountsToApiEntries(sessionForm.counts),
              notes: sessionForm.notes?.trim() || "",
            }
        : {
            expected_balance: Number(
              sessionForm.expectedBalance ||
                activeHistorySession?.expectedBalance ||
                activeBox.currentSession?.expectedBalance ||
                activeBox.currentSession?.openingBalance ||
                0
            ),
            closing_counts: mapCountsToApiEntries(sessionForm.counts),
            change_total: Number(sessionForm.changeAmount) || 0,
            notes: sessionForm.notes?.trim() || "",
          };

      const response = await apiFetch(endpoint, {
        method: isEditOpenSession || isEditClosedSession ? "PATCH" : "POST",
        headers: apiHeaders,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || "No se pudo guardar la sesión de caja.");
      }

      setSessionDrafts((prev) => {
        const next = { ...prev };
        delete next[sessionDraftKey(activeBox.id, modalState.type)];
        return next;
      });
      await refreshBoxes();
      resetModal();
    } catch (error) {
      setErrorMessage(error.message || "No se pudo guardar la sesión de caja.");
    } finally {
      setSaving(false);
    }
  }

  function toggleCollaborator(collaboratorId) {
    setBoxForm((prev) => {
      const exists = prev.collaborators.includes(collaboratorId);
      return {
        ...prev,
        collaborators: exists
          ? prev.collaborators.filter((item) => item !== collaboratorId)
          : [...prev.collaborators, collaboratorId],
      };
    });
  }

  function updateCount(field, value) {
    setSessionForm((prev) => ({
      ...prev,
      counts: {
        ...prev.counts,
        [field]: Math.max(0, Number(value || 0)),
      },
    }));
  }


  const headerButtons = [
    { label: "Nueva caja", color: "green", onClick: openCreateBoxModal },
  ];

  return (
    <div className="space-y-4">
      <ButtonBar title="Gestión de cajas" buttons={headerButtons} />

      {errorMessage ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-4">
        <SummaryCard label="Cajas activas" value={summary.openCount} tone="emerald" />
        <SummaryCard label="Total cajas" value={summary.totalBoxes} tone="blue" />
        <SummaryCard label="Cierres registrados" value={summary.closedSessions} tone="amber" />
        <SummaryCard
          label="Diferencia acumulada"
          value={formatMoney(summary.totalDifference)}
          tone={summary.totalDifference >= 0 ? "emerald" : "red"}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.2fr_1fr]">
        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Cajas</h3>
              <p className="text-sm text-gray-500">
                Crea cajas, asígnalas a colaboradores y abre o cierra turno con recuento.
              </p>
              {!canEditCajaMoney ? (
                <p className="mt-2 text-sm text-amber-700">
                  Puedes corregir el recuento de una caja <strong>abierta</strong>. Los cierres ya
                  registrados solo los puede editar el owner.
                </p>
              ) : null}
            </div>
          </div>

          <div className="space-y-3">
            {loading ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-6 py-10 text-center text-sm text-gray-600">
                Cargando cajas...
              </div>
            ) : boxes.length === 0 ? (
              <EmptyState
                title="Todavía no hay cajas"
                description="Empieza creando una caja para esta tienda. Después podrás abrirla con saldo inicial y registrar el recuento de cierre."
              />
            ) : (
              boxes.map((box) => {
                const isOpen = box.currentSession?.status === "open";
                const session = box.currentSession;
                return (
                  <div key={box.id} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-lg font-semibold text-gray-900">{box.name}</div>
                          <StatusBadge open={isOpen} />
                          <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-600">
                            Zona: {box.zone || "Todas"}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Colaboradores:{" "}
                          {box.collaborators?.length ? box.collaborators.join(", ") : "Sin asignar"}
                        </div>
                        {isOpen ? (
                          <div className="grid gap-2 sm:grid-cols-3">
                            <MiniMetric
                              label="Abierta"
                              value={new Date(session.openedAt).toLocaleString("es-ES")}
                            />
                            <MiniMetric label="Saldo inicial" value={formatMoney(session.openingBalance)} />
                            <MiniMetric
                              label="Recuento apertura"
                              value={formatMoney(session.openingCountedTotal)}
                            />
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500">
                            Último cierre:{" "}
                            {box.sessions?.[0]?.closedAt
                              ? new Date(box.sessions[0].closedAt).toLocaleString("es-ES")
                              : "Sin historial"}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <ActionButton
                          icon={<HiOutlinePencil size={16} />}
                          label="Editar"
                          tone="slate"
                          onClick={() => {
                            if (isOpen && canEditOpenCaja) {
                              openEditOpenSessionModal(box);
                              return;
                            }
                            openEditBoxModal(box);
                          }}
                        />
                        {isOpen ? (
                          <ActionButton
                            icon={<HiOutlineArchive size={16} />}
                            label="Cerrar caja"
                            tone="amber"
                            onClick={() => openSessionModal(box, "close-session")}
                          />
                        ) : (
                          <ActionButton
                            icon={<HiOutlinePlus size={16} />}
                            label="Abrir caja"
                            tone="emerald"
                            onClick={() => openSessionModal(box, "open-session")}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Sugerencia operativa</h3>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Lo más sólido es que cada caja tenga un único turno abierto a la vez y que el cierre
              guarde dos datos: <strong>saldo esperado</strong> y <strong>recuento real</strong>.
              Así la diferencia queda registrada aunque las ventas se controlen en otro módulo.
            </p>
          </div>
          <div className="space-y-3 rounded-xl bg-gray-50 p-4 text-sm text-gray-700">
            <div>1. Crear caja y asignar colaboradores responsables.</div>
            <div>2. Abrir caja con saldo inicial y recuento de monedas/billetes.</div>
            <div>3. Cerrar caja indicando saldo esperado y recuento real.</div>
            <div>4. Revisar diferencias en el histórico antes de consolidarlas en contabilidad.</div>
          </div>
        </section>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Historial</h3>
          <p className="text-sm text-gray-500">
            {venueName ? `Cajas abiertas y cierres en ${venueName}.` : "Cajas abiertas y cierres por caja."}
          </p>
        </div>

        {historyRows.length === 0 ? (
          <EmptyState
            title="Sin movimientos todavía"
            description="Cuando abras o cierres una caja aparecerá aquí el resumen del recuento y la diferencia."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-3">Caja</th>
                  <th className="px-3 py-3">Apertura</th>
                  <th className="px-3 py-3">Cierre</th>
                  <th className="px-3 py-3">Esperado</th>
                  <th className="px-3 py-3">Recuento</th>
                  <th className="px-3 py-3">Diferencia</th>
                  <th className="px-3 py-3">Cambio</th>
                  <th className="px-3 py-3">Retirada</th>
                  <th className="px-3 py-3">Notas</th>
                  <th className="px-3 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {historyRows.map((item) => (
                  <tr key={item.id} className={`border-t border-gray-100 ${item.isOpen ? "bg-emerald-50/40" : ""}`}>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">{item.boxName}</span>
                        {item.isOpen ? <StatusBadge open /> : null}
                      </div>
                      <div className="text-xs text-gray-500">{item.zone}</div>
                    </td>
                    <td className="px-3 py-3 text-gray-600">
                      {new Date(item.openedAt).toLocaleString("es-ES")}
                    </td>
                    <td className="px-3 py-3 text-gray-600">
                      {item.isOpen ? (
                        <span className="text-emerald-700">En curso</span>
                      ) : item.closedAt ? (
                        new Date(item.closedAt).toLocaleString("es-ES")
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-3 py-3 font-medium text-gray-800">
                      {formatMoney(item.expectedBalance)}
                    </td>
                    <td className="px-3 py-3 font-medium text-gray-800">
                      {formatMoney(item.isOpen ? item.openingCountedTotal : item.closingCountedTotal)}
                    </td>
                    <td className="px-3 py-3">
                      {item.isOpen ? (
                        <span className="text-gray-400">-</span>
                      ) : (
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            Number(item.difference || 0) >= 0
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {formatMoney(item.difference)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-700">
                      {item.isOpen ? <span className="text-gray-400">-</span> : formatMoney(item.changeTotal)}
                    </td>
                    <td className="px-3 py-3 font-medium text-gray-800">
                      {item.isOpen ? <span className="text-gray-400">-</span> : formatMoney(item.withdrawal)}
                    </td>
                    <td className="px-3 py-3 text-gray-600">
                      {item.closingNotes || item.notes || "-"}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {item.isOpen ? (
                        canEditOpenCaja ? (
                          <button
                            type="button"
                            onClick={() => {
                              const box = boxes.find((b) => b.id === item.boxId);
                              if (box) openEditOpenSessionModal(box);
                            }}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            <HiOutlinePencil size={15} />
                            Editar caja abierta
                          </button>
                        ) : null
                      ) : canEditCajaMoney ? (
                        <button
                          type="button"
                          onClick={() => openEditHistorySessionModal(item)}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <HiOutlinePencil size={15} />
                          Editar cierre
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {modalState.type === "create-box" || modalState.type === "edit-box" ? (
        <ModalShell
          title={modalState.type === "create-box" ? "Crear nueva caja" : `Editar caja ${activeBox?.name || ""}`}
          onClose={resetModal}
        >
          <form onSubmit={upsertBox} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <div className="mb-1 text-sm font-medium text-gray-700">Nombre</div>
                <input
                  value={boxForm.name}
                  onChange={(event) => setBoxForm((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                  placeholder="Caja principal"
                />
              </label>
              <label className="block">
                <div className="mb-1 text-sm font-medium text-gray-700">Zona</div>
                <input
                  value={boxForm.zone}
                  onChange={(event) => setBoxForm((prev) => ({ ...prev, zone: event.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                  placeholder="Todas"
                />
              </label>
            </div>

            <div>
              <div className="mb-2 text-sm font-medium text-gray-700">Colaboradores asignados</div>
              {collaborators.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500">
                  No se han cargado colaboradores desde staff/owners. Puedes dejar la caja sin asignar por ahora.
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {collaborators.map((collaborator) => {
                    const selected = boxForm.collaborators.includes(collaborator.id);
                    return (
                      <button
                        key={collaborator.id}
                        type="button"
                        onClick={() => toggleCollaborator(collaborator.id)}
                        className={`rounded-lg border px-3 py-2 text-left text-sm transition ${
                          selected
                            ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {collaborator.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-between gap-3">
              <div>
                {modalState.type === "edit-box" ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {activeBox?.currentSession?.status === "open" && canEditOpenCaja ? (
                      <button
                        type="button"
                        onClick={() => openEditOpenSessionModal(activeBox)}
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <HiOutlinePencil size={16} />
                        Editar dinero caja abierta
                      </button>
                    ) : null}
                    {/* Borrar una caja con historial arrastra sus cierres (cascade) → se
                        bloquea para no perder el historial. Las cajas sin cierres sí. */}
                    {(activeBox?.sessions?.length || 0) > 0 ? (
                      <span className="text-xs text-gray-500">
                        Esta caja tiene historial de cierres y no se puede borrar.
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={deleteBox}
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <HiOutlineTrash size={16} />
                        Eliminar caja
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={resetModal}
                  disabled={saving}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving
                    ? "Guardando..."
                    : modalState.type === "create-box"
                      ? "Crear caja"
                      : "Actualizar caja"}
                </button>
              </div>
            </div>
          </form>
        </ModalShell>
      ) : null}

      {modalState.type === "open-session" || modalState.type === "close-session" || modalState.type === "edit-open-session" || modalState.type === "edit-closed-session" ? (
        <ModalShell
          title={
            modalState.type === "open-session"
              ? `Abrir caja ${activeBox?.name || ""}`
              : modalState.type === "edit-open-session"
                ? `Editar caja abierta ${activeBox?.name || ""}`
              : modalState.type === "edit-closed-session"
                ? `Editar cierre ${activeBox?.name || ""}`
                : `Cerrar caja ${activeBox?.name || ""}`
          }
          onClose={() => closeSessionModal({ preserveDraft: true })}
        >
          <form onSubmit={saveSession} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              {modalState.type === "open-session" || modalState.type === "edit-open-session" ? (
                <label className="block">
                  <div className="mb-1 text-sm font-medium text-gray-700">
                    {modalState.type === "edit-open-session" ? "Saldo inicial" : "Saldo inicial esperado"}
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={sessionForm.openingBalance}
                    onChange={(event) =>
                      setSessionForm((prev) => ({ ...prev, openingBalance: event.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                  />
                </label>
              ) : (
                <label className="block">
                  <div className="mb-1 text-sm font-medium text-gray-700">Saldo esperado al cierre</div>
                  <input
                    type="number"
                    step="0.01"
                    value={sessionForm.expectedBalance}
                    onChange={(event) =>
                      setSessionForm((prev) => ({ ...prev, expectedBalance: event.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                  />
                </label>
              )}

              <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                <div className="text-sm font-medium text-blue-800">Total del recuento</div>
                <div className="mt-1 text-2xl font-semibold text-blue-900">
                  {formatMoney(calculateCountsTotal(sessionForm.counts))}
                </div>
                <div className="mt-1 text-xs text-blue-700">
                  {modalState.type === "open-session"
                    ? "Este total se guarda como recuento de apertura."
                    : modalState.type === "edit-open-session"
                      ? "Edita el saldo y el recuento de la caja abierta antes del cierre."
                    : modalState.type === "edit-closed-session"
                      ? "Edita el recuento del cierre ya registrado y recalcula la diferencia."
                      : "Se comparará con el saldo esperado para calcular la diferencia."}
                </div>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <DenominationFieldset
                title="Recuento monedas"
                denominations={COIN_DENOMINATIONS}
                counts={sessionForm.counts}
                onChange={updateCount}
              />
              <DenominationFieldset
                title="Recuento billetes"
                denominations={NOTE_DENOMINATIONS}
                counts={sessionForm.counts}
                onChange={updateCount}
              />
            </div>

            {modalState.type === "close-session" || modalState.type === "edit-closed-session" ? (
              <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
                <div>
                  <div className="text-sm font-semibold text-amber-900">Cambio a dejar en la caja</div>
                  <div className="text-xs text-amber-700">
                    Objetivo orientativo: {formatMoney(IDEAL_CHANGE)}. La retirada (sobre que va al
                    banco) = total contado − cambio que dejas. El cambio queda como saldo de apertura
                    del siguiente turno.
                  </div>
                </div>
                <label className="block max-w-xs">
                  <div className="mb-1 text-sm font-medium text-gray-700">Cambio a dejar (€)</div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={sessionForm.changeAmount ?? ""}
                    onChange={(event) => setSessionForm((prev) => ({ ...prev, changeAmount: event.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                    placeholder={String(IDEAL_CHANGE)}
                  />
                </label>
                {(() => {
                  const total = calculateCountsTotal(sessionForm.counts);
                  const change = Math.max(0, Number(sessionForm.changeAmount) || 0);
                  const retirada = total - change;
                  return (
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="rounded-lg border border-amber-200 bg-white px-3 py-2">
                        <div className="text-xs text-gray-500">Total contado</div>
                        <div className="text-lg font-semibold text-gray-900">{formatMoney(total)}</div>
                      </div>
                      <div className="rounded-lg border border-amber-200 bg-white px-3 py-2">
                        <div className="text-xs text-gray-500">Cambio que dejas</div>
                        <div className="text-lg font-semibold text-gray-900">{formatMoney(change)}</div>
                      </div>
                      <div className="rounded-lg border border-amber-300 bg-amber-100 px-3 py-2">
                        <div className="text-xs text-amber-800">Retirada (al banco)</div>
                        <div className={`text-lg font-semibold ${retirada < 0 ? "text-red-700" : "text-amber-900"}`}>
                          {formatMoney(retirada)}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : null}

            <label className="block">
              <div className="mb-1 text-sm font-medium text-gray-700">Notas</div>
              <textarea
                value={sessionForm.notes}
                onChange={(event) => setSessionForm((prev) => ({ ...prev, notes: event.target.value }))}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                placeholder="Ej.: cambio inicial, incidencias, diferencias justificadas..."
              />
            </label>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => closeSessionModal({ preserveDraft: true })}
                disabled={saving}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60 ${
                  modalState.type === "open-session"
                    ? "bg-emerald-500 hover:bg-emerald-600"
                    : "bg-amber-500 hover:bg-amber-600"
                }`}
              >
                {saving
                  ? "Guardando..."
                  : modalState.type === "open-session"
                    ? "Abrir caja"
                    : modalState.type === "edit-open-session"
                      ? "Actualizar caja abierta"
                    : modalState.type === "edit-closed-session"
                      ? "Actualizar cierre"
                      : "Cerrar caja"}
              </button>
            </div>
          </form>
        </ModalShell>
      ) : null}
    </div>
  );
}

function SummaryCard({ label, value, tone = "blue" }) {
  const tones = {
    blue: "border-blue-200 bg-blue-50 text-blue-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    red: "border-red-200 bg-red-50 text-red-900",
  };

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${tones[tone] || tones.blue}`}>
      <div className="text-sm font-medium opacity-80">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function StatusBadge({ open }) {
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
        open ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-700"
      }`}
    >
      {open ? "Abierta" : "Cerrada"}
    </span>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-gray-800">{value}</div>
    </div>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
      <div className="text-lg font-semibold text-gray-800">{title}</div>
      <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-gray-600">{description}</p>
    </div>
  );
}

function ActionButton({ icon, label, tone = "slate", onClick }) {
  const tones = {
    slate: "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
    emerald: "border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600",
    amber: "border-amber-500 bg-amber-500 text-white hover:bg-amber-600",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${tones[tone] || tones.slate}`}
    >
      {icon}
      {label}
    </button>
  );
}
